import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaUndoAlt, 
  FaShieldAlt, 
  FaTimesCircle, 
  FaMoneyCheckAlt, 
  FaCalendarCheck, 
  FaExclamationCircle, 
  FaCreditCard, 
  FaEnvelope, 
  FaBuilding, 
  FaCheckCircle
} from 'react-icons/fa';
import NavbarComponent from './NavbarComponent';
import Footer from './Footer';
import './Policies.css';

const RefundPolicy = ({ isLoggedIn, username, userRole, handleLogout }) => {
  return (
    <div className="policy-page-container">
      <NavbarComponent 
        isLoggedIn={isLoggedIn} 
        username={username} 
        userRole={userRole} 
        handleLogout={handleLogout} 
      />

      {/* Hero Section */}
      <section className="policy-hero">
        <div className="policy-badge">
          <FaUndoAlt /> Legal & Compliance
        </div>
        <h1>Refund Policy</h1>
        <p>
          We want you to be completely satisfied with your learning journey on CodingBoss. 
          Review our refund guidelines, eligibility windows, and settlement timelines.
        </p>
        <span className="policy-effective-date">Last Updated: September 2026</span>

        {/* Tab Navigation */}
        <div className="policy-nav-tabs">
          <Link to="/privacy-policy" className="policy-tab-link">
            <FaShieldAlt /> Privacy Policy
          </Link>
          <Link to="/cancellation-policy" className="policy-tab-link">
            <FaTimesCircle /> Cancellation Policy
          </Link>
          <Link to="/refund-policy" className="policy-tab-link active">
            <FaUndoAlt /> Refund Policy
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <main className="policy-main-content">
        <div className="policy-card">

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaMoneyCheckAlt /> 1. Overview
            </h2>
            <p className="policy-text">
              At <strong>CodingBoss</strong>, customer satisfaction and quality learning experiences are paramount. 
              Our Refund Policy is crafted to be fair, transparent, and compliant with consumer protection standards and 
              payment gateway regulations (such as Razorpay, Stripe, and RBI guidelines).
            </p>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaCalendarCheck /> 2. Eligibility for Refunds
            </h2>
            <p className="policy-text">You are eligible to request a refund under the following conditions:</p>
            <ul className="policy-list">
              <li>
                <strong>Live Bootcamps & Cohorts:</strong> If you request cancellation at least 
                <strong> 48 hours before</strong> the official start of the first live class, you are eligible for a 
                <strong> 100% refund</strong> (excluding nominal payment gateway processing charges, if applicable).
              </li>
              <li>
                <strong>Self-Paced Courses:</strong> If requested within <strong>24 hours of purchase</strong>, provided 
                that you have accessed less than 15% of the course videos and no digital certificates have been generated.
              </li>
              <li>
                <strong>Course Cancellation by Us:</strong> If CodingBoss cancels a batch, workshop, or technical hackathon, 
                you are entitled to an immediate <strong>full 100% refund</strong>.
              </li>
            </ul>

            <div className="policy-highlight-box success">
              <h4>7-Day Money-Back Guarantee on Flagship Programs</h4>
              <p>
                Selected flagship programs marked with the "Money-Back Guarantee" badge allow you to attend the first 2 live 
                sessions. If you feel the course is not the right fit, notify us within 7 days of the start date for a hassle-free refund.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaExclamationCircle /> 3. Non-Refundable Scenarios
            </h2>
            <p className="policy-text">Refunds will strictly not be entertained in the following situations:</p>
            <ul className="policy-list">
              <li>Requests submitted after a batch has commenced beyond the stipulated refund window.</li>
              <li>College or university assessment portal fees after an exam or test has been attempted.</li>
              <li>Any program where a verifiable certificate of completion has already been issued.</li>
              <li>Failure to attend live classes, submit assignments, or complete assessments due to personal scheduling conflicts.</li>
              <li>Suspension of account due to plagiarism, cheating in proctored tests, or violation of CodingBoss community standards.</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaCreditCard /> 4. Duplicate Transactions & Payment Failures
            </h2>
            <p className="policy-text">
              In case of a payment failure or accidental duplicate transaction where your account was debited multiple times:
            </p>
            <ul className="policy-list">
              <li>
                <strong>Duplicate Charges:</strong> If an excess payment occurred, the surplus amount will be automatically 
                refunded in full upon internal payment gateway verification.
              </li>
              <li>
                <strong>Payment Debited but Not Reflected:</strong> Most banks auto-reconcile failed transactions within 
                24 to 48 hours. If the enrollment is not activated, forward your transaction reference ID to 
                <strong> info@codingboss.com</strong> and we will resolve it immediately.
              </li>
            </ul>
          </div>

          <div className="policy-section">
            <h2 className="policy-section-title">
              <FaCheckCircle /> 5. Refund Processing Timeline & Method
            </h2>
            <p className="policy-text">
              All approved refunds are handled professionally through secure banking channels:
            </p>
            <ul className="policy-list">
              <li>
                <strong>Mode of Refund:</strong> Refunds are credited strictly back to the 
                <strong> original payment method</strong> used during checkout (Credit Card, Debit Card, UPI, Net Banking, or Wallet).
              </li>
              <li>
                <strong>Processing Timeline:</strong> Once approved, the refund will be initiated by our finance department within 
                <strong> 2 to 3 business days</strong>.
              </li>
              <li>
                <strong>Bank Settlement:</strong> Depending on your banking partner or card issuer, it typically takes 
                <strong> 5 to 7 working days</strong> for the funds to reflect in your bank statement.
              </li>
            </ul>

            <div className="policy-highlight-box info">
              <h4>Tax & Gateway Deductions</h4>
              <p>
                Government taxes (GST) and non-refundable third-party payment gateway merchant fees (typically 2-3%) may be 
                deducted from the gross refund value where permitted by law, unless the refund is initiated due to a fault on our part.
              </p>
            </div>
          </div>

          {/* Contact Details */}
          <div className="policy-contact-card">
            <h4>Have Questions Regarding a Refund?</h4>
            <p className="policy-text" style={{ marginBottom: 0 }}>
              Write directly to our billing and finance desk with your invoice number and payment receipt:
            </p>
            <div className="policy-contact-grid">
              <a href="mailto:info@codingboss.com" className="contact-info-item">
                <FaEnvelope /> info@codingboss.com
              </a>
              <div className="contact-info-item">
                <FaBuilding /> CodingBoss Finance & Billing
              </div>
              <Link to="/cancellation-policy" className="contact-info-item">
                <FaTimesCircle /> Cancellation Policy
              </Link>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPolicy;
